const BRANCH_ID_KEY = 'current-branch-id';
const BRANCH_DATA_KEY = 'branch-data-cache';
const BRANCH_MAP_KEY = 'branch-map';
const CACHE_TTL_MS = 0;

export interface BranchCacheData {
  branchInfo: BranchInfo | null;
  products: Product[];
  storefrontChannel: StorefrontChannel | null;
}

interface CachedBranchData {
  branchId: string;
  data: BranchCacheData;
  timestamp: number;
}

import type { StorefrontChannel } from '@/lib/api';
import type { BranchInfo, Product } from '@/types/product';

export const branchCache = {
  saveBranchId(branchId: string): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(BRANCH_ID_KEY, branchId);
  },

  getSavedBranchId(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(BRANCH_ID_KEY);
  },

  saveBranchMap(customUrl: string, branchId: string): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(BRANCH_MAP_KEY);
      const map: Record<string, string> = raw ? JSON.parse(raw) : {};
      map[customUrl] = branchId;
      sessionStorage.setItem(BRANCH_MAP_KEY, JSON.stringify(map));
    } catch {
      console.warn('Failed to save branch map');
    }
  },

  getBranchIdForCustomUrl(customUrl: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(BRANCH_MAP_KEY);
      if (!raw) return null;
      const map: Record<string, string> = JSON.parse(raw);
      return map[customUrl] ?? null;
    } catch {
      return null;
    }
  },

  saveBranchData(branchId: string, data: BranchCacheData): void {
    if (typeof window === 'undefined') return;
    const cacheData: CachedBranchData = {
      branchId,
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(BRANCH_DATA_KEY, JSON.stringify(cacheData));
  },

  getSavedBranchData(branchId: string): BranchCacheData | null {
    if (typeof window === 'undefined') return null;
    try {
      const cached = sessionStorage.getItem(BRANCH_DATA_KEY);
      if (!cached) return null;
      const parsed: CachedBranchData = JSON.parse(cached);
      const isValid =
        parsed.branchId === branchId &&
        Date.now() - parsed.timestamp < CACHE_TTL_MS;
      return isValid ? parsed.data : null;
    } catch {
      return null;
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(BRANCH_ID_KEY);
    sessionStorage.removeItem(BRANCH_DATA_KEY);
    sessionStorage.removeItem(BRANCH_MAP_KEY);
  },
};
